import {Button, Typography, Link, MenuItem} from '@material-ui/core';
import {
  getEnrollmentFilesNotProcessed,
  changeFilesNotProcessedFilters,
  FilterEnum,
} from 'Enrollment/ducks/enrollmentFilesNotProcessed';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {copyToClipboard} from 'Shared/utilities/CopyToClipboard';
import {toFormattedDateTime} from 'Shared/utilities/DateFormatter';
import {
  withStringFilterProperties,
  withDateFilterProperties,
  withSelectFilterProperties,
  withInequalityFilterProperties,
} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import EnrollmentStatusModal from './EnrollmentStatusModal';
import './EnrollmentFilesNotProcessed.sass';
import {ModalState, showModal} from 'Core/ducks/modal';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import {createSnackbar} from 'Core/ducks/snackbar';
import {toFormattedTimeDuration} from 'Shared/utilities/DateFormatter';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {
  navigateToFileHistory,
  navigateToFileProfile,
} from 'Enrollment/components/EnrollmentFilesNotProcessed/EnrollmentFileNotProcessedUtilities';
import {EnrollmentFileNotes} from 'Enrollment/models/EnrollmentFileNotes';
import {FileExtension} from 'Shared/components/FileDownloadButton';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import ModularHost from 'Shared/components/ModularHost/ModularHost';
import ToolbarHeader, {ExportToolbarHeaderIcon} from 'Shared/components/Toolbar/ToolbarHeader';

const fileAcceptanceStatuses: FilterEnum[] = [
  {
    name: 'Recognized',
    value: 'Recognized',
  },
  {
    name: 'Unrecognized',
    value: 'Unrecognized',
  },
  {
    name: 'Quarantined',
    value: 'Quarantined',
  },
];

const scheduleHistoryStatuses: FilterEnum[] = [
  {
    name: 'Met',
    value: 'Met',
  },
  {
    name: 'Unmet',
    value: 'Unmet',
  },
  {
    name: 'Off Schedule',
    value: 'OffSchedule',
  },
  {
    name: 'Already Met',
    value: 'AlreadyMet',
  },
  {
    name: 'Unscheduled',
    value: 'Unscheduled',
  },
  {
    name: 'Empty Unmet',
    value: 'EmptyUnMet',
  },
];

const EnrollmentFilesNotProcessed = () => {
  const dispatch = useDispatch();
  const {enrollmentFilesNotProcessedPage, filter, enrollmentFileStatus} = useSelector(
    (state: ReducerState) => state.unprocessedEnrollmentFiles
  );

  const changeFilter = withValueChangedHook(filter, changeFilesNotProcessedFilters, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: filter,
  };

  const [showStatus, hideStatus] = useState(false);

  useEffect(() => {
    dispatch(getEnrollmentFilesNotProcessed(undefined, filter));
  }, [dispatch, filter]);

  const handleNavigateToFileProfile = (fileProfileId?: string) => {
    navigateToFileProfile(fileProfileId);
  };

  const handleNavigateToFileProfileNotes = (notes?: EnrollmentFileNotes[]) => {
    if (notes) dispatch(showModal(ModalState.EnrollmentFilesNotProcessedNotes({notes: notes})));
  };

  const handleNavigateToFileHistory = (fileHistoryId?: string) => {
    navigateToFileHistory(fileHistoryId);
  };

  const getAFLConfigurationUrl = (): string => {
    return `${process.env.REACT_APP_PipelinePortal_AFL_Url}`;
  };

  const copyFilePath = (filePath?: string) => {
    if (filePath) {
      const copyActionResult = copyToClipboard(filePath)();
      if (copyActionResult) dispatch(createSnackbar('Path Successfully Copied to Clipboard!'));
      else dispatch(createSnackbar('Path Copy Failed!'));
    }
  };

  const timeSinceFileSaved = (fileSavedDate: Date | undefined) => {
    const today = new Date();
    return pipe(
      O.fromNullable(fileSavedDate),
      O.map(() => toFormattedTimeDuration(fileSavedDate, today)),
      O.getOrElse(() => 'N/A')
    );
  };

  return (
    <>
      <div className="afl-configuration">
        <Link
          id="files-not-processed-afl-configuration"
          color="primary"
          target="aflConfiguration"
          href={getAFLConfigurationUrl()}
        >
          AFL Configuration
        </Link>
      </div>
      <ModularHost>
        <ToolbarHeader title="Files Not Processed">
          <ExportToolbarHeaderIcon
            title="Files Not Processed"
            downloadedFileName="FilesNotProcessed"
            extension={FileExtension.CSV}
            relativeFileLocation={`/enrollmentFiles/processingHistory/pending${addFilterParameters({
              ...filter,
              exportToCsv: true,
            })}`}
            totalRecordCount = {0}
          />
        </ToolbarHeader>
        <PagedView
          pagedViewKey="enrollmentFilesNotProcessed"
          data={enrollmentFilesNotProcessedPage}
          loadMore={page => dispatch(getEnrollmentFilesNotProcessed(page, filter))}
        >
          {columnProps => (
            <>
              <Column
                {...columnProps}
                title="File Name"
                filterProperties={withStringFilterProperties(filterProperties, 'fileName')}
                default
              >
                {enrollmentFileNotProcessed => enrollmentFileNotProcessed.fileName}
              </Column>
              <Column
                {...columnProps}
                title="Folder Path"
                filterProperties={withStringFilterProperties(filterProperties, 'relativeFilePath')}
                default
              >
                {enrollmentFileNotProcessed => enrollmentFileNotProcessed.relativeFilePath}
              </Column>
              <Column
                {...columnProps}
                title="File Saved Date"
                filterProperties={withDateFilterProperties(filterProperties, 'fileSavedDate')}
                default
              >
                {enrollmentFileNotProcessed => toFormattedDateTime(enrollmentFileNotProcessed.fileSavedDate)}
              </Column>
              <Column {...columnProps} title="Age in Days" default>
                {enrollmentFileNotProcessed => timeSinceFileSaved(enrollmentFileNotProcessed.fileSavedDate)}
              </Column>
              <Column
                {...columnProps}
                title="File Schedule"
                filterProperties={withSelectFilterProperties(
                  filterProperties,
                  'scheduleHistoryStatus',
                  scheduleHistoryStatuses
                )}
                default
              >
                {enrollmentFileNotProcessed => (
                  <Typography variant="body2">{enrollmentFileNotProcessed.scheduleHistoryStatus}</Typography>
                )}
              </Column>
              <Column
                {...columnProps}
                title="File Profile"
                filterProperties={withSelectFilterProperties(
                  filterProperties,
                  'fileAcceptanceStatus',
                  fileAcceptanceStatuses
                )}
                default
              >
                {enrollmentFileNotProcessed => (
                  <Button
                    color="primary"
                    onClick={() => handleNavigateToFileProfile(enrollmentFileNotProcessed.fileProfileId!)}
                  >
                    {enrollmentFileNotProcessed.fileAcceptanceStatus!.name}
                  </Button>
                )}
              </Column>
              <Column
                {...columnProps}
                title="Notes"
                filterProperties={withInequalityFilterProperties(filterProperties, 'notesCount')}
              >
                {enrollmentFileNotProcessed =>
                  enrollmentFileNotProcessed.notesCount! > 0 ? (
                    <Link
                      variant="caption"
                      component="button"
                      onClick={() => handleNavigateToFileProfileNotes(enrollmentFileNotProcessed.notes!)}
                    >
                      <Typography variant="body2"> {enrollmentFileNotProcessed.notesCount}</Typography>
                    </Link>
                  ) : (
                    enrollmentFileNotProcessed.notesCount
                  )
                }
              </Column>
              <Column {...columnProps} title="Actions" isActions default>
                {enrollmentFileNotProcessed => (
                  <div>
                    <ActionsMenu>
                      <div>
                        <MenuItem
                          id="menu-item-actions-file-history"
                          onClick={() => handleNavigateToFileHistory(enrollmentFileNotProcessed.fileHistoryId!)}
                        >
                          File History
                        </MenuItem>
                        <MenuItem
                          id="menu-item-actions-copy-folder-location"
                          color="primary"
                          onClick={() => copyFilePath(enrollmentFileNotProcessed.folderPath!)}
                        >
                          Copy Folder Location
                        </MenuItem>
                        <MenuItem
                          id="menu-item-actions-copy-file-location"
                          color="primary"
                          onClick={() => copyFilePath(enrollmentFileNotProcessed.filePath!)}
                        >
                          Copy File Location
                        </MenuItem>
                      </div>
                    </ActionsMenu>
                  </div>
                )}
              </Column>
            </>
          )}
        </PagedView>
      </ModularHost>
      <EnrollmentStatusModal onClose={() => hideStatus(false)} open={showStatus} data={enrollmentFileStatus} />
    </>
  );
};

export default EnrollmentFilesNotProcessed;
